import Link from 'next/link';
import { getImageUrl, hasCurrentBlockOutDates, hasFutureBlockOutDates } from 'utils/utils';
import { Item } from 'types';
import tw from '../../../tailwind.config';

const getCollectionUrl = (category: string) => {
  switch (category) {
    case 'Vest':
    case 'Cummerbund':
      return 'vests-and-cummerbunds';
    case 'Shirt':
      return 'shirts';
    case 'Tie':
      return 'ties';
    case 'Suspenders':
    case 'Belt':
    case 'Lapel Pin':
    case 'Tie Bar':
    case 'Cufflinks':
    case 'Pocket Square':
      return 'accessories';
    case 'Shoe':
    case 'Socks':
      return 'shoes-and-socks';
    default:
      return null;
  }
};

const getItemUrl = (item: Item) => {
  if (item.category !== 'preconfigured') {
    return `/collection/${getCollectionUrl(item.category!)}/${item.url_slug}`;
  }

  return `/collection/tuxedos-and-suits/${item.url_slug}`;
};

const displayPrice = (product: Item) => {
  if (product.category !== 'preconfigured') {
    return `$${product.cost}`;
  }

  if (!product.is_retail) {
    if (product.retail_bundle && product.retail_bundle.displayable) {
      return `Rent $${product.cost} / Buy $${product.retail_bundle.cost}`;
    } else {
      return `Rent $${product.cost}`;
    }
  }

  if (!product.rental_bundle) {
    return `Buy $${product.cost}`;
  }
};

interface Props {
  product: Item;
  tuxOrSuits: boolean;
}

function CollectionListItem({ product, tuxOrSuits }: Props) {
  const label = 'PDP_Hero';

  const imageUrl = getImageUrl(product, label);

  const generalBlockoutDates = product.blockout_dates?.filter((date) => date.availability === 'general');
  const hasLimitedAvailability = hasCurrentBlockOutDates(generalBlockoutDates ?? []) || hasFutureBlockOutDates(generalBlockoutDates ?? []);

  const comingSoon = product.blockout_dates?.filter((date) => date.availability === 'coming-soon') ?? [];
  const hasComingSoonAvailability = hasCurrentBlockOutDates(comingSoon);

  const imgSrcSet = (size: number) =>
    `${encodeURI(imageUrl!)}${
      tuxOrSuits ? `?auto=format,compress&fit=crop&w=${size}&ar=5:7` : `?auto=format,compress&fit=crop&w=${size}&ar=1`
    } 1x, ${encodeURI(imageUrl!)}${
      tuxOrSuits ? `?auto=format&fit=crop&w=${size}&ar=5:7&dpr=2&q=30` : `?auto=format&fit=crop&w=${size}&ar=1&dpr=2&q=30`
    } 2x`;
  return (
    <div
      className={`
          col-span-6
          flex
          flex-col
          ${tuxOrSuits ? 'sm:col-span-6' : 'sm:col-span-4'}
          ${tuxOrSuits ? 'md:col-span-4' : 'md:col-span-3'}
        `}
    >
      <Link
        href="/collection/[category]/[product]"
        as={getItemUrl(product)}
        className="group relative z-10 flex flex-auto flex-col transition hover:z-50 hover:no-underline"
      >
        <div
          className={`
          ${tuxOrSuits ? 'bg-gray p-16 group-hover:bg-gray-lighter group-hover:shadow-4 sm:p-24' : 'bg-transparent'}
          
            flex
            flex-auto
            flex-col
            duration-200
            ease-in-out
          `}
        >
          <div className={`${tuxOrSuits ? 'flex-grow' : ''} mb-8 flex flex-col justify-center`}>
            <div className={`relative ${tuxOrSuits ? 'aspect-ratio-5by7' : 'aspect-ratio-1by1'} `}>
              <picture>
                <source media={`(min-width: ${tw.theme.screens.lg})`} srcSet={imgSrcSet(308)} />
                <source media={`(min-width: ${tw.theme.screens.md})`} srcSet={imgSrcSet(288)} />
                <source media={`(min-width: ${tw.theme.screens.sm})`} srcSet={imgSrcSet(234)} />
                <source media={`(min-width: ${tw.theme.screens.xs})`} srcSet={imgSrcSet(187)} />
                <source srcSet={imgSrcSet(263)} />
                <img
                  src={`${imageUrl}${
                    tuxOrSuits ? '?auto=format,compress&fit=crop&w=263&ar=5:7' : '?auto=format,compress&fit=crop&w=263&ar=1'
                  }`}
                  alt={product.display_name}
                  loading="lazy"
                  className={`
                    block
                    w-full
                    transition
                    group-hover:opacity-30
                  `}
                />
              </picture>
              <div
                className={`
                  absolute
                  left-[50%]
                  top-[50%]
                  translate-x-[-50%]
                  translate-y-[100%]
                  transform
                  whitespace-nowrap
                  bg-white
                  p-16
                  text-black
                  opacity-0
                  shadow-3
                  duration-200
                  ease-in-out
                  group-hover:translate-y-[-50%]
                  group-hover:opacity-100
              `}
                style={{ willChange: 'transform, opacity' }}
              >
                <h4 className="text-h6">View Details</h4>
              </div>
            </div>
          </div>
          {hasLimitedAvailability && !hasComingSoonAvailability && (
            <div className="mb-8 inline-block self-start bg-black px-8 py-4 lg:mb-16">
              <p className="text-sm leading-tight text-white">Limited Availability</p>
            </div>
          )}

          {hasComingSoonAvailability && (
            <div className="mb-8 inline-block self-start bg-black px-8 py-4 lg:mb-16">
              <p className="text-sm leading-tight text-white">Coming Soon</p>
            </div>
          )}

          <h2 className="text-h4 mb-4 group-hover:text-blue-dark">{product.display_name}</h2>
          <div>
            <p
              className={`mr-8 inline ${
                Number(product.cost!) < 100 && product.category === 'preconfigured' ? 'text-red' : 'text-gray-darker'
              }`}
            >
              {displayPrice(product)}
            </p>
          </div>
        </div>
      </Link>
    </div>
  );
}

export default CollectionListItem;
